// src\components\portal\SummaryCards.tsx
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Star, TrendingUp, AlertTriangle } from "lucide-react";

interface Structure {
  id: string;
  name: string;
  type: string;
  slug: string;
  compliance: number;
  locality: string;
  accessibility: {
    motoria: number;
    sensoriale: number;
    cognitiva: number;
    alimentare: number;
  };
  totalScorePercent?: number;
}

const VERTICALS = [
  { key: 'motoria', label: 'Motoria', valueClassName: 'text-blue-600' },
  { key: 'sensoriale', label: 'Sensoriale', valueClassName: 'text-sky-600' },
  { key: 'cognitiva', label: 'Cognitiva', valueClassName: 'text-cyan-600' },
  { key: 'alimentare', label: 'Alimentare', valueClassName: 'text-green-600' }
] as const;

type VerticalKey = typeof VERTICALS[number]['key'];

interface TopPerformersCardProps {
  structures: Structure[];
  onViewPassport?: (slug: string) => void;
}

interface NeedsAttentionCardProps {
  structures: Structure[];
  onViewPassport?: (slug: string) => void;
}

interface PortalOverviewCardProps {
  structures: Structure[];
  portalName: string;
  totalSuggestions?: number;
}

export function TopPerformersCard({ structures, onViewPassport }: TopPerformersCardProps) {
  // Calculate average accessibility score for each structure and sort by highest
  const topPerformers = structures
    .map(structure => {
      const scores = VERTICALS.map((vertical) => structure.accessibility?.[vertical.key] ?? 0);
      const fallbackAverage = scores.reduce((sum, score) => sum + score, 0) / scores.length;
      const averageScore = typeof structure.totalScorePercent === 'number'
        ? structure.totalScorePercent
        : fallbackAverage;
      return {
        ...structure,
        averageScore: Math.round(averageScore)
      };
    })
    .sort((a, b) => b.averageScore - a.averageScore)
    .slice(0, 5);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Star className="w-5 h-5 text-yellow-500" />
          Migliori performance
        </CardTitle>
        <CardDescription>Strutture con i punteggi di accessibilità più alti</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {topPerformers.length > 0 ? (
            topPerformers.map((structure) => (
              <div key={structure.id} className="flex flex-col items-left justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex-1">
                  <p className="font-medium text-s">{structure.name}</p>
                  <p className="text-ss text-muted-foreground">
                    Punteggio: {structure.averageScore}% • {structure.locality}
                  </p>
                </div>
                <div className="flex justify-end gap-2">
                  <Badge className="bg-green-100 text-green-800 text-ss">
                    {structure.averageScore >= 90 ? 'Eccellente' : 'Buono'}
                  </Badge>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-4 text-muted-foreground text-s">
              Nessuna struttura disponibile
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function NeedsAttentionCard({ structures, onViewPassport }: NeedsAttentionCardProps) {
  // Calculate average accessibility score for each structure and sort by lowest
  const needsAttention = structures
    .map(structure => {
      const scores = VERTICALS.map((vertical) => structure.accessibility?.[vertical.key] ?? 0);
      const fallbackAverage = scores.reduce((sum, score) => sum + score, 0) / scores.length;
      const averageScore = typeof structure.totalScorePercent === 'number'
        ? structure.totalScorePercent
        : fallbackAverage;
      return {
        ...structure,
        averageScore: Math.round(averageScore)
      };
    })
    .sort((a, b) => a.averageScore - b.averageScore)
    .slice(0, 5);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-orange-500" />
          Richiede attenzione
        </CardTitle>
        <CardDescription>Strutture che richiedono interventi prioritari</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {needsAttention.length > 0 ? (
            needsAttention.map((structure) => (
              <div key={structure.id} className="flex flex-col items-left justify-between p-3 bg-orange-50 rounded-lg border border-orange-200">
                <div className="flex-1">
                  <p className="font-medium text-s">{structure.name}</p>
                  <p className="text-ss text-muted-foreground">
                    Punteggio: {structure.averageScore}% • {structure.locality}
                  </p>
                </div>
                <div className="flex justify-end gap-2">
                  <Badge variant="outline" className="text-ss border-orange-300 text-orange-700">
                    {structure.averageScore < 70 ? 'Critico' : 'Da migliorare'}
                  </Badge>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-4 text-muted-foreground text-s">
              Nessuna struttura disponibile
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function PortalOverviewCard({ structures, portalName, totalSuggestions }: PortalOverviewCardProps) {
  const totalFacilities = structures.length;
  const suggestionsTotal = typeof totalSuggestions === 'number' ? totalSuggestions : 0;
  const counters = VERTICALS.map((vertical) => ({
    ...vertical,
    count: structures.filter((structure) => (structure.accessibility?.[vertical.key as VerticalKey] ?? 0) >= 85).length
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Panoramica portale</CardTitle>
        <CardDescription>Metriche principali per {portalName}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-s text-muted-foreground">Strutture totali</span>
            <span className="font-medium">{totalFacilities}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-s text-muted-foreground">Suggerimenti totali</span>
            <span className="font-medium">{suggestionsTotal}</span>
          </div>
          <div className="pt-4 border-t">
            <h4 className="font-medium mb-3">Performance per verticale di accessibilità</h4>
            <div className="space-y-2 text-s">
              {counters.map((counter) => (
                <div key={counter.key} className="flex items-center justify-between">
                  <span className="text-muted-foreground">{counter.label} (&gt;=85%)</span>
                  <span className={`font-medium ${counter.valueClassName}`}>{counter.count}/{totalFacilities}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
